import { POGO_COLOURS, SIZE_GROUPS } from "./pogoConfig";

// Product data shaped like a Shopify product (handle/options/images/price).
// Components read ONLY from this object — when Shopify connects later, swap
// this static object for Storefront API data without touching the UI.
export const PRODUCT = {
  handle: "posso-work-clog",
  title: "POSSO Work Clog",
  subtitle: "Made to stand out. Built to withstand.",
  description:
    "Slip-resistant, closed-toe footwear made for long days on your feet — in colours that don't look clinical.",
  price: 119,
  // Founding preorder offer: $119 now, regular $130 after launch (planned
  // future price — never claim it was sold at $130). Save $11.
  launchPrice: 130,
  // Work-bestie bundle: from 2 pairs, bundleDiscountAmount comes off once
  // (2 pairs = $199). Must match the discount created in Shopify admin —
  // set bundleDiscountCode below to auto-apply it at checkout.
  priceNote: "Launch price",
  currency: "AUD",
  // Shopify wiring (lib/shopify.js reads only this block).
  shopify: {
    handle: "posso-work-clog",
    colourOption: "Color",
    sizeOption: "Shoe size",
    // Storefront shows EU women's sizes; Shopify variants use US women's.
    euToUs: { "35": "5", "36": "6", "37": "7", "38": "8", "39": "9", "40": "10", "41": "11", "42": "12" },
    // Bundle: $39 off when 2+ pairs (2 x $119 - $39 = $199).
    bundleDiscountAmount: 39,
    // Optional Shopify discount code auto-applied at checkout for 2+ pairs.
    bundleDiscountCode: null,
  },
  // Per-colour product imagery — swatches connect directly to these.
  colourImages: {
    pink: "/images/colours/pink.webp",
    yellow: "/images/colours/yellow.webp",
    "light-blue": "/images/colours/light-blue.webp",
    lilac: "/images/colours/lilac.webp",
    black: "/images/colours/black.webp",
  },
  images: [
    {
      src: "/images/colours/pink.webp",
      alt: "POSSO Work Clog in Pink",
    },
  ],
  options: [
    { name: "Colour", type: "colour", values: POGO_COLOURS },
    { name: "Size", type: "size", groups: SIZE_GROUPS },
  ],
  features: [
    {
      title: "All-shift comfort",
      body: "Designed around long hours on your feet.",
      bg: "#FFD84D",
    },
    {
      title: "Slip-resistant grip",
      body: "For wet and unpredictable work environments.",
      bg: "#7CC9FF",
    },
    {
      title: "Closed-toe design",
      body: "More coverage for the messy reality of work.",
      bg: "#FF7A59",
    },
    {
      title: "Easy clean",
      body: "Because spills happen.",
      bg: "#C6A2FF",
    },
    {
      title: "Colours you actually want to wear",
      body: "Work shoes don't have to look boring.",
      bg: "#FF6BAA",
    },
  ],
  // Placeholders only — no invented specifications. Fill before preorder opens.
  details: [
    ["Construction", "Lightweight EVA construction. Final material spec confirmed before preorder."],
    ["Outsole", "Slip-resistant outsole."],
    ["Upper", "Closed, easy-clean upper."],
    ["Insole", "Supportive insole. Removable-insole spec confirmed before preorder."],
    ["Design", "Work-focused design for long shifts on your feet."],
    ["Colours", "Multiple colour options at launch."],
    ["Care", "Wipes clean. Full care guide ships with your pair."],
  ],
};

// Gallery slides — the selected colourway's studio shot takes the lead
// slot, followed by feature/on-foot photography generated from the real
// studio shots. All ~6:5 so the stage never changes dimensions.
// srcSet gives mobile an 800w variant (~5x smaller); desktop keeps the
// original. GALLERY_SIZES mirrors the layout: full-bleed mobile, ~half of
// max-w-6xl on desktop.
export const GALLERY_SIZES = "(max-width: 639px) 100vw, 560px";
const rs = (base) =>
  `/images/${base}-480.webp 480w, /images/${base}-800.webp 800w, /images/${base}.webp 1200w`;
export const GALLERY = [
  { shot: "SIDE PROFILE", src: "/images/colours/pink-800.webp", srcSet: rs("colours/pink"), alt: "POSSO Work Clog in Pink — side profile", studio: true },
  { shot: "FRONT 3/4", src: "/images/pink-front34-800.webp", srcSet: rs("pink-front34"), alt: "POSSO Work Clog in Pink — front three-quarter view", studio: true },
  { shot: "ON FOOT", src: "/images/on-foot-feetup-800.webp", srcSet: "/images/on-foot-feetup-480.webp 480w, /images/on-foot-feetup-800.webp 800w, /images/on-foot-feetup.webp?v=2 1293w", alt: "Feet-up break on the ward — pink POSSO Work Clogs with navy scrubs", studio: true, caption: "Cute", icon: "sparkles" },
  { shot: "THE CREW", src: "/images/crew-soles-800.webp", srcSet: "/images/crew-soles-480.webp 480w, /images/crew-soles-800.webp 800w, /images/crew-soles.webp 941w", alt: "Three nurses on a locker-room bench showing the soles of their pink, blue and yellow POSSO clogs", pos: "center 60%", fit: "blur", caption: "So comfy", icon: "heart" },
  { shot: "OUTSOLE", src: "/images/feature-grip-800.webp", srcSet: "/images/feature-grip-480.webp 480w, /images/feature-grip-800.webp 800w, /images/feature-grip.webp 1080w", alt: "Close-up of the POSSO slip-resistant outsole tread", studio: true, caption: "Slip resistant", icon: "footprints" },
  { shot: "INSOLE", src: "/images/insole-800.webp", srcSet: "/images/insole-480.webp 480w, /images/insole-800.webp 800w, /images/insole.webp?v=2 1400w", alt: "POSSO arch-support insole with honeycomb-textured top and pink arch pad", studio: true, caption: "Gel insole", icon: "cloud" },
  { shot: "ANATOMY", src: "/images/anatomy-800.webp", srcSet: rs("anatomy"), alt: "Cutaway of the pink POSSO clog showing its reinforced toe cap, gel arch insole and slip-resistant outsole", studio: true },
];


// PDP FAQ — answers use ONLY confirmed project information. [PLACEHOLDER]
// entries are editable stand-ins until final policies/specs are supplied.
export const PRODUCT_FAQS = [
  { q: "How do POSSOs fit?", a: "POSSO is listed in European (EU) women's sizing — 35 to 42. Check the size guide for foot lengths in cm to find your fit.", sizeGuide: true },
  { q: "Are POSSOs slip resistant?", a: "They're built with a slip-resistant outsole designed for wet, unpredictable workplace floors." },
  { q: "What is the toe made from?", a: "A closed-toe design for everyday coverage. Final material specifications are confirmed before pre-order opens." },
  { q: "How do I clean my POSSOs?", a: "They wipe clean. A full care guide ships with your pair." },
  { q: "When will my pre-order ship?", a: "Estimated dispatch: 15th of December — they'll be waiting under the Christmas tree. Secure your pair now 💗 Preorder now to lock in your POSSO clogs before our official launch. First drop quantities are limited! You'll be notified once we ship." },
  { q: "Can I exchange my size?", a: "Yes — one size exchange per order within 30 days of delivery, as long as your POSSOs are clean, unworn outdoors and in resaleable condition. See our Returns & Exchanges Policy for the full details." },
  { q: "What are POSSOs made from?", a: "Lightweight EVA construction with a closed, easy-clean upper. Final material specs are confirmed before pre-order opens." },
  { q: "Where do you ship?", a: "Australia-wide — and shipping is FREE on every order." },
  { q: "What if I order the wrong size?", a: "You can request a size exchange within 30 days of delivery. Just make sure your POSSOs are clean, unworn outdoors and in resaleable condition." },
  { q: "Do I have to pay for exchange shipping?", a: "You cover postage back to us, and we'll cover standard shipping for your first replacement pair." },
  { q: "What if my size is sold out?", a: "We'll help you find another available size, arrange store credit, or wait for a restock." },
  { q: "Does this apply to preorders?", a: "Absolutely! Your 30-day size-exchange window begins when your POSSOs arrive." },
  { q: "What if my shoes are faulty?", a: "Contact us and we'll help resolve the issue in accordance with Australian Consumer Law." },
];

// Social proof: no genuine review data exists yet — keep hidden.
// Flip to true and supply real tester content when it arrives.
export const SHOW_SOCIAL_PROOF = false;

// Confirmed specs only — [TO BE CONFIRMED] where data doesn't exist yet.
export const SPECS = [
  ["Upper material", "Closed, easy-clean upper. Final material spec confirmed before pre-order."],
  ["Outsole", "Slip-resistant outsole."],
  ["Slip resistance", "Designed for wet, unpredictable workplace floors."],
  ["Toe construction", "Closed-toe design for everyday coverage."],
  ["Cleaning", "Wipes clean. Full care guide ships with your pair."],
  ["Fit", "European sizing — women's 35–42. See the size guide for foot lengths in cm."],
];
